import React, { useState, useRef, useLayoutEffect, useContext } from "react";
import { Card, Typography, Row, Col, Divider, Tag, Button, message, Empty, Pagination } from "antd";
import useServerPagination from "Common/hooks/useServerPagination";
import {
  SafetyCertificateOutlined,
  UserOutlined,
  CalendarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { formatDateOnly } from "Common/Components/FormatDateTime";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CertificateOfCompletion from "../../Admin/TrainingCertificate/trainingCertificate";
import { ProfileContext } from "Common/hooks/context/ProfileContext";
const { Title, Text } = Typography;

// Fetch a page of the current user's training certificates (role-based endpoint).
const fetchCertificates = async ({ pageNumber, pageSize, orderByLatest }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const personalId = localStorage.getItem("personalId");
  let url = "";
  if (role === "Mentor") {
    url = `${process.env.REACT_APP_API_URL}/TrainingCertificate/Mentor/List?mentorId=${personalId}`;
  } else if (role === "Learner") {
    url = `${process.env.REACT_APP_API_URL}/TrainingCertificate/Learner/List?learnerId=${personalId}`;
  } else {
    return { items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 1 };
  }
  url += `&pageNumber=${pageNumber}&pageSize=${pageSize}&orderByLatest=${orderByLatest}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "*/*",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const TrainingCertificates = () => {
  const [downloading, setDownloading] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;
  const { items: certificates, loading, paginationProps } = useServerPagination(fetchCertificates);
  const getScoreColor = (score) => {
    if (score >= 80) return "green";
    if (score >= 50) return "orange";
    return "red";
  };
  const handleDownload = (cert) => {
    setCurrentCertificate(cert);
    setDownloading(true);
  };
  const certificateRef = useRef(null);
  useLayoutEffect(() => {
    const generatePDF = async () => {
      if (!currentCertificate || !certificateRef.current) return;
      try {
        // Add a slight delay to ensure rendering is complete
        await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms delay
        const canvas = await html2canvas(certificateRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("landscape", "mm", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        // Fit the whole certificate inside one page (aspect preserved, centered)
        // so the bottom rows (course, score, issue date, id) are never cut off.
        const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
        const drawW = imgProps.width * ratio;
        const drawH = imgProps.height * ratio;
        pdf.addImage(imgData, "PNG", (pageWidth - drawW) / 2, (pageHeight - drawH) / 2, drawW, drawH);
        pdf.save(
          `${currentCertificate.firstName}_${currentCertificate.lastName
          }_${currentCertificate.trainingTitle.replace(/\s+/g, "_")}.pdf`
        );
      } catch (error) {
        console.error("Error generating PDF:", error);
        message.error("Failed to generate certificate. Please try again.");
      } finally {
        setDownloading(false);
        setCurrentCertificate(null);
      }
    };
    if (currentCertificate) {
      generatePDF();
    }
  }, [currentCertificate]);
  return (
    <>
      {currentCertificate && (
        <div style={{ position: "absolute", left: "-9999px" }}>
          <div ref={certificateRef}>
            <CertificateOfCompletion certificate={currentCertificate} />
          </div>
        </div>
      )}
      <Card loading={loading} title="Training Certificates" className="card-professional">
        <div
          style={{
            pointerEvents: isBlocked ? "none" : "auto",
            opacity: isBlocked ? 0.6 : 1,
          }}
        >
          {certificates.length === 0 ? (
            <Empty description="There are no training certificates available." />
          ) : (
            <Row gutter={[16, 16]}>
              {certificates.map((cert) => (
                <Col xs={24} sm={12} lg={8} key={cert.id}>
                  <Card
                    hoverable
                    style={{ borderRadius: "8px", height: "100%" }}
                    cover={
                      <div
                        style={{
                          background: "#9A6B0E",
                          padding: "16px",
                          textAlign: "center",
                          color: "white",
                        }}
                      >
                        <SafetyCertificateOutlined style={{ fontSize: "48px" }} />
                        <Title level={4} style={{ color: "white", margin: "8px 0 0" }}>
                          Certificate of Completion
                        </Title>
                      </div>
                    }
                    actions={[
                      <Button
                        key={`download-${cert.id}`}
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(cert)}
                        disabled={isBlocked}
                        loading={downloading && currentCertificate?.id === cert.id}
                      >
                        Download Certificate
                      </Button>
                    ]}
                  >
                    <div style={{ textAlign: "center" }}>
                      <Title level={5} style={{ marginBottom: "8px" }}>
                        {cert.trainingTitle}
                      </Title>
                      <div style={{ marginBottom: "16px" }}>
                        <Tag icon={<UserOutlined />} color="gold">
                          {cert.firstName} {cert.lastName}
                        </Tag>
                      </div>
                      <Divider style={{ margin: "16px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Text strong>Score:</Text>
                        <Tag color={getScoreColor(cert.finalScore)}>
                          {(cert.finalScore ?? 0).toFixed(2)}% ({cert.totalMarks ?? 0} marks)
                        </Tag>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <Text strong>Completed On:</Text>
                        <Tag icon={<CalendarOutlined />}>{formatDateOnly(cert.createdOn)}</Tag>
                      </div>
                      <div style={{ marginTop: "16px" }}>
                        <Text type="secondary">Certificate ID: {cert.id}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          {certificates.length > 0 && (
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <Pagination {...paginationProps} />
            </div>
          )}
        </div>
      </Card>
    </>
  );
};
export default TrainingCertificates;
